/**
 * useUsersManagement Hook
 *
 * Encapsulates all business logic for user & invitation management:
 * - Fetching users and pending invitations
 * - Combining them into unified table rows
 * - Mutations (deactivate, reactivate, delete, resend, cancel)
 * - Modal state management (invite, edit, confirm)
 * - Toast notifications for success / error feedback
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import {
  CancelInvitationDocument,
  DeactivateUserDocument,
  DeleteAccountUserDocument,
  GetAccountUsersDocument,
  GetPendingInvitationsDocument,
  ReactivateUserDocument,
  ResendInvitationDocument
} from '@/graphql';
import type { InvitationData, TableRow, UserData } from '../components/system-settings/users/types';
import { getUserManagementErrorKey } from '../utils/map-user-management-error';

export type ConfirmAction =
  | { type: 'deactivate'; user: UserData }
  | { type: 'reactivate'; user: UserData }
  | { type: 'delete'; user: UserData }
  | { type: 'cancelInvitation'; invitation: InvitationData };

export function useUsersManagement() {
  const t = useFeatureTranslation('systemSettings.users');
  const { showSuccess, showError } = useToastHelpers();

  // ── Modal state ──────────────────────────────────────────────
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  // ── Queries ──────────────────────────────────────────────────
  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
    refetch: refetchUsers
  } = useQuery(GetAccountUsersDocument, { fetchPolicy: 'network-only' });

  const {
    data: invitationsData,
    loading: invitationsLoading,
    error: invitationsError,
    refetch: refetchInvitations
  } = useQuery(GetPendingInvitationsDocument, { fetchPolicy: 'network-only' });

  // ── Mutations ────────────────────────────────────────────────
  const [deactivateUser, { loading: deactivating }] = useMutation(DeactivateUserDocument);
  const [reactivateUser, { loading: reactivating }] = useMutation(ReactivateUserDocument);
  const [deleteUser, { loading: deleting }] = useMutation(DeleteAccountUserDocument);
  const [resendInvitation, { loading: resending }] = useMutation(ResendInvitationDocument);
  const [cancelInvitation, { loading: canceling }] = useMutation(CancelInvitationDocument);

  const isActionLoading = deactivating || reactivating || deleting || resending || canceling;

  // ── Error toast on query failure ─────────────────────────────
  useEffect(() => {
    if (usersError || invitationsError) {
      showError(t('errors.loadError'));
    }
    // showError is a stable reference; t is stable per mount.
    // We only want to react to new error instances.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersError, invitationsError]);

  // ── Unified table rows ───────────────────────────────────────
  const tableRows = useMemo<TableRow[]>(() => {
    const rows: TableRow[] = [];

    if (usersData?.accountUsersConnection?.edges) {
      for (const { node: user } of usersData.accountUsersConnection.edges) {
        rows.push({
          type: 'user',
          data: {
            id: user.id,
            email: user.email ?? '',
            firstName: user.firstName,
            lastName: user.lastName,
            displayName: user.displayName,
            roles: [...user.roles],
            status: user.status,
            isSystemUser: user.isSystemUser,
            identitySharedWithOtherAccounts: user.identitySharedWithOtherAccounts,
            createdAt: user.createdAt,
            orgUnits: user.orgUnits
          }
        });
      }
    }

    if (invitationsData?.pendingInvitations?.invitations) {
      for (const invitation of invitationsData.pendingInvitations.invitations) {
        rows.push({
          type: 'invitation',
          data: {
            id: invitation.id,
            email: invitation.email,
            roles: [...invitation.roles],
            expiresAt: invitation.expiresAt,
            createdAt: invitation.createdAt,
            invitedBy: invitation.invitedBy
              ? { displayName: invitation.invitedBy.displayName }
              : null
          }
        });
      }
    }

    return rows;
  }, [usersData, invitationsData]);

  // ── Handlers (stable references) ────────────────────────────
  const refetchAll = useCallback(() => {
    refetchUsers();
    refetchInvitations();
  }, [refetchUsers, refetchInvitations]);

  const openInviteModal = useCallback(() => setShowInviteModal(true), []);
  const closeInviteModal = useCallback(() => setShowInviteModal(false), []);

  const openEditModal = useCallback((user: UserData) => setEditingUser(user), []);
  const closeEditModal = useCallback(() => setEditingUser(null), []);

  const closeConfirmAction = useCallback(() => setConfirmAction(null), []);

  const handleInviteSuccess = useCallback(() => {
    setShowInviteModal(false);
    showSuccess(t('success.inviteSuccess'));
    refetchAll();
  }, [showSuccess, t, refetchAll]);

  const handleEditSuccess = useCallback(() => {
    setEditingUser(null);
    showSuccess(t('success.updateSuccess'));
    refetchAll();
  }, [showSuccess, t, refetchAll]);

  const handleResendInvitation = useCallback(
    async (invitation: InvitationData) => {
      try {
        const result = await resendInvitation({
          variables: { invitationId: invitation.id }
        });
        if (result.data?.resendInvitation.success) {
          showSuccess(t('success.resendSuccess'));
        } else {
          showError(result.data?.resendInvitation.message || t('errors.resendError'));
        }
      } catch (err) {
        showError(err instanceof Error ? err.message : t('errors.resendError'));
      }
    },
    [resendInvitation, showSuccess, showError, t]
  );

  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction) return;

    try {
      switch (confirmAction.type) {
        case 'deactivate': {
          const result = await deactivateUser({
            variables: { userId: confirmAction.user.id }
          });
          if (result.data?.deactivateUser.success) {
            showSuccess(t('success.deactivateSuccess'));
          } else {
            showError(result.data?.deactivateUser.message || t('errors.deactivateError'));
          }
          break;
        }
        case 'reactivate': {
          const result = await reactivateUser({
            variables: { userId: confirmAction.user.id }
          });
          if (result.data?.reactivateUser.success) {
            showSuccess(t('success.reactivateSuccess'));
          } else {
            showError(result.data?.reactivateUser.message || t('errors.reactivateError'));
          }
          break;
        }
        case 'delete': {
          const result = await deleteUser({
            variables: { userId: confirmAction.user.id }
          });
          if (result.data?.deleteAccountUser.success) {
            showSuccess(t('success.deleteSuccess'));
          } else {
            showError(result.data?.deleteAccountUser.message || t('errors.deleteError'));
          }
          break;
        }
        case 'cancelInvitation': {
          const result = await cancelInvitation({
            variables: { invitationId: confirmAction.invitation.id }
          });
          if (result.data?.cancelInvitation.success) {
            showSuccess(t('success.cancelSuccess'));
          } else {
            showError(result.data?.cancelInvitation.message || t('errors.cancelError'));
          }
          break;
        }
      }
      refetchAll();
    } catch (err) {
      const mappedKey = getUserManagementErrorKey(err);
      if (mappedKey) {
        showError(t(mappedKey));
      } else {
        const errorKey =
          confirmAction.type === 'cancelInvitation' ? 'cancelError' : `${confirmAction.type}Error`;
        showError(err instanceof Error ? err.message : t(`errors.${errorKey}`));
      }
    } finally {
      setConfirmAction(null);
    }
  }, [
    confirmAction,
    deactivateUser,
    reactivateUser,
    deleteUser,
    cancelInvitation,
    refetchAll,
    showSuccess,
    showError,
    t
  ]);

  // ── Table callback handlers (stable references) ─────────────
  const handleDeactivate = useCallback(
    (user: UserData) => setConfirmAction({ type: 'deactivate', user }),
    []
  );

  const handleReactivate = useCallback(
    (user: UserData) => setConfirmAction({ type: 'reactivate', user }),
    []
  );

  const handleDelete = useCallback(
    (user: UserData) => setConfirmAction({ type: 'delete', user }),
    []
  );

  const handleCancelInvitation = useCallback(
    (invitation: InvitationData) => setConfirmAction({ type: 'cancelInvitation', invitation }),
    []
  );

  return {
    // Data
    tableRows,
    loading: usersLoading || invitationsLoading,
    error: usersError || invitationsError,

    // Modal state
    showInviteModal,
    editingUser,
    confirmAction,
    isActionLoading,

    // Modal controls
    openInviteModal,
    closeInviteModal,
    openEditModal,
    closeEditModal,
    closeConfirmAction,

    // Action handlers
    handleInviteSuccess,
    handleEditSuccess,
    handleConfirmAction,
    handleResendInvitation,
    handleDeactivate,
    handleReactivate,
    handleDelete,
    handleCancelInvitation
  } as const;
}
